import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import {
  CtaDictionaryItemDto,
  useCreateCtaDictionaryItemMutation,
  useGetCtaDictionaryAdminOptionsQuery,
  useUpdateCtaDictionaryItemMutation,
} from '~/features/dictionaries/model/api/cta-dictionary-api';
import { CTA_FALLBACK_LANGUAGE_OPTIONS } from '~/shared/const/callToAction';

type CtaDictionaryModalProps = {
  open: boolean;
  handleClose: () => void;
  editingItem?: CtaDictionaryItemDto | null;
  onSaved?: () => void;
};

export const CtaDictionaryModal = ({
  open,
  handleClose,
  editingItem,
  onSaved,
}: CtaDictionaryModalProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { data } = useGetCtaDictionaryAdminOptionsQuery();
  const languages = data?.data?.languages?.length
    ? data.data.languages.map(item => ({ code: item.code, label: item.label }))
    : CTA_FALLBACK_LANGUAGE_OPTIONS.map(item => ({ code: item.value, label: item.label }));
  const defaultLanguageCode = data?.data?.default_language_code || 'en-US';

  const [formState, setFormState] = useState({
    key: '',
    sort: '100',
    is_active: true,
    texts: {} as Record<string, string>,
  });

  const [createItem, { isLoading: isCreating }] = useCreateCtaDictionaryItemMutation();
  const [updateItem, { isLoading: isUpdating }] = useUpdateCtaDictionaryItemMutation();
  const isLoading = isCreating || isUpdating;

  const title = editingItem ? 'Редактировать CTA' : 'Добавить CTA';

  useEffect(() => {
    const nextTexts = languages.reduce<Record<string, string>>((acc, language) => {
      acc[language.code] = editingItem?.texts?.[language.code] ?? '';
      return acc;
    }, {});

    setFormState({
      key: editingItem?.key ?? '',
      sort: String(editingItem?.sort ?? 100),
      is_active: editingItem?.is_active ?? true,
      texts: nextTexts,
    });
  }, [editingItem, languages, open]);

  const normalizedTranslations = useMemo(() => {
    return languages.map(language => ({
      language_code: language.code,
      text: String(formState.texts[language.code] || '').trim(),
    }));
  }, [formState.texts, languages]);

  const handleSave = async () => {
    const key = formState.key.trim().toUpperCase();

    if (!key) {
      enqueueSnackbar('Укажите key CTA.', {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      return;
    }

    const defaultTranslation = normalizedTranslations.find(item => item.language_code === defaultLanguageCode)?.text || '';
    if (!defaultTranslation) {
      enqueueSnackbar('Заполните CTA для языка по умолчанию.', {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      return;
    }

    const payload = {
      key,
      sort: Number(formState.sort) || 0,
      is_active: formState.is_active,
      translations: normalizedTranslations,
    };

    try {
      if (editingItem?.id) {
        await updateItem({ id: editingItem.id, ...payload }).unwrap();
      } else {
        await createItem(payload).unwrap();
      }

      enqueueSnackbar('CTA успешно сохранен.', {
        variant: 'success',
        autoHideDuration: 3000,
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      handleClose();
      onSaved?.();
    } catch (error: any) {
      const validationErrors = error?.data?.errors;
      const validationMessage = validationErrors
        ? Object.values(validationErrors).flat().join('\n')
        : error?.data?.message || 'Не удалось сохранить CTA.';

      enqueueSnackbar(validationMessage, {
        variant: 'error',
        autoHideDuration: 5000,
        style: { maxWidth: '700px', whiteSpace: 'pre-wrap' },
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          borderRadius: '12px',
          boxShadow: 24,
          width: 760,
          maxWidth: '95vw',
          maxHeight: '90vh',
          overflowY: 'auto',
          p: 3,
        }}
      >
        <Stack spacing={2}>
          <Typography variant="h6">{title}</Typography>

          <Box>
            <Typography fontWeight={600} variant="subtitle2" sx={{ mb: 0.5 }}>
              Key
            </Typography>
            <TextField
              value={formState.key}
              onChange={e =>
                setFormState(prev => ({
                  ...prev,
                  key: e.target.value.replace(/\s+/g, '_').toUpperCase(),
                }))
              }
              disabled={isLoading}
              fullWidth
              placeholder="LEARN_MORE"
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Используйте формат `UPPER_SNAKE_CASE`, например `LEARN_MORE`.
            </Typography>
          </Box>

          <Box>
            <Typography fontWeight={600} variant="subtitle2" sx={{ mb: 0.5 }}>
              Сортировка
            </Typography>
            <TextField
              type="number"
              value={formState.sort}
              onChange={e => setFormState(prev => ({ ...prev, sort: e.target.value }))}
              disabled={isLoading}
              fullWidth
            />
          </Box>

          <Stack direction="row" alignItems="center" spacing={1}>
            <Switch
              checked={formState.is_active}
              onChange={e => setFormState(prev => ({ ...prev, is_active: e.target.checked }))}
              disabled={isLoading}
            />
            <Typography>{formState.is_active ? 'Активен' : 'Выключен'}</Typography>
          </Stack>

          <Typography variant="subtitle2">
            Переводы CTA
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Обязателен только текст для языка по умолчанию. Пустые переводы будут временно заполнены значением по умолчанию.
          </Typography>
          <Stack spacing={1.5}>
            {languages.map(language => (
              <Box key={language.code}>
                <Typography fontWeight={600} variant="subtitle2" sx={{ mb: 0.5 }}>
                  {language.label}
                </Typography>
                <TextField
                  value={formState.texts[language.code] || ''}
                  onChange={e =>
                    setFormState(prev => ({
                      ...prev,
                      texts: {
                        ...prev.texts,
                        [language.code]: e.target.value,
                      },
                    }))
                  }
                  disabled={isLoading}
                  fullWidth
                  placeholder={language.code === defaultLanguageCode ? 'Обязательный перевод' : 'Можно заполнить позже'}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {language.code === defaultLanguageCode ? 'Язык по умолчанию' : 'Можно заполнить позже'}
                </Typography>
              </Box>
            ))}
          </Stack>

          <Stack direction="row" spacing={1.5}>
            <Button fullWidth variant="outlined" onClick={handleClose} disabled={isLoading}>
              Отмена
            </Button>
            <Button fullWidth variant="contained" onClick={handleSave} disabled={isLoading}>
              {isLoading ? <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} /> : null}
              Сохранить
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Modal>
  );
};
